import { useEffect, useState } from "react";
import { CreditCard, ArrowsClockwise, FunnelSimple } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const STATUSES = ["initiated", "paid", "expired", "failed"];

const STATUS_COLOR = {
  paid: "bg-emerald-50 text-emerald-700",
  initiated: "bg-amber-50 text-amber-700",
  expired: "bg-rose-50 text-rose-700",
  failed: "bg-rose-50 text-rose-700",
};

export default function AdminPayments() {
  const [tx, setTx] = useState([]);
  const [stats, setStats] = useState({ total: 0, paid: 0, initiated: 0, revenue_by_currency: [] });
  const [events, setEvents] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : "";
      const [t, s, e] = await Promise.all([
        api.get(`/admin/payments${qs}`),
        api.get("/admin/payments/stats"),
        api.get("/admin/payments/events?limit=20"),
      ]);
      setTx(t.data); setStats(s.data); setEvents(e.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [statusFilter]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="admin-payments">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2">
            <CreditCard size={14} weight="duotone" /> Payments
          </div>
          <h2 className="font-display text-2xl text-gos-charcoal">{stats.total} transactions</h2>
        </div>
        <button onClick={refresh} data-testid="payments-refresh" className="btn-secondary text-xs !py-2">
          <ArrowsClockwise size={14} weight="bold" /> Refresh
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="Paid" value={stats.paid} />
        <Kpi label="Initiated" value={stats.initiated} />
        <Kpi label="Webhook events" value={events.length} />
        <Kpi label="Currencies" value={stats.revenue_by_currency.length} />
      </div>

      {stats.revenue_by_currency.length > 0 && (
        <div className="bg-white border border-gos-border rounded-sm p-4 mb-6">
          <div className="overline mb-3">Revenue by currency</div>
          <div className="flex flex-wrap gap-3">
            {stats.revenue_by_currency.map((r) => (
              <div key={r.currency} className="text-center px-4 py-2 border border-gos-border rounded-sm" data-testid={`pay-rev-${r.currency}`}>
                <div className="text-xs uppercase tracking-widest text-gos-muted">{r.currency?.toUpperCase()}</div>
                <div className="font-display text-xl text-gos-charcoal">{Number(r.total).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                <div className="text-[10px] text-gos-muted">{r.count} paid</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-gos-border rounded-sm p-4 mb-4 flex flex-wrap gap-2 items-center">
        <FunnelSimple size={14} className="text-gos-slate" />
        <span className="overline">Status</span>
        <button onClick={() => setStatusFilter("")} data-testid="pay-filter-all" className={`px-3 py-1 text-xs uppercase tracking-widest rounded-sm border ${!statusFilter ? "bg-gos-navy text-white border-gos-navy" : "border-gos-border text-gos-slate"}`}>All</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)} data-testid={`pay-filter-${s}`}
            className={`px-3 py-1 text-xs uppercase tracking-widest rounded-sm border ${statusFilter === s ? "bg-gos-navy text-white border-gos-navy" : "border-gos-border text-gos-slate"}`}
          >{s}</button>
        ))}
      </div>

      <div className="bg-white border border-gos-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gos-subtle text-xs uppercase tracking-widest text-gos-navy">
            <tr>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Session</th>
              <th className="text-left p-3">Invoice</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-left p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-6 text-center text-gos-muted">Loading…</td></tr>}
            {!loading && tx.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-gos-muted">No transactions yet.</td></tr>
            )}
            {tx.map((t) => (
              <tr key={t.id} data-testid={`pay-row-${t.session_id?.slice(0, 12)}`} className="border-t border-gos-border">
                <td className="p-3 text-xs text-gos-muted">{(t.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="p-3 font-mono text-xs text-gos-slate">{t.session_id?.slice(0, 22)}…</td>
                <td className="p-3 text-xs text-gos-slate">{t.metadata?.invoice_number || "—"}</td>
                <td className="p-3 text-right text-sm text-gos-charcoal">{t.currency?.toUpperCase()} {Number(t.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm ${STATUS_COLOR[t.payment_status] || "bg-gos-subtle"}`}>
                    {t.payment_status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {events.length > 0 && (
        <div className="mt-8">
          <div className="overline mb-2">Recent Stripe Events</div>
          <div className="bg-white border border-gos-border rounded-sm p-3 max-h-72 overflow-y-auto">
            {events.map((ev) => (
              <div key={ev.id} className="text-xs border-b border-gos-border last:border-b-0 py-1.5 flex justify-between gap-3">
                <span className="font-mono text-gos-muted truncate">{ev.event_id}</span>
                <span className="text-gos-slate truncate">{ev.event_type}</span>
                <span className={`px-1.5 rounded-sm ${STATUS_COLOR[ev.payment_status] || "bg-gos-subtle"}`}>{ev.payment_status}</span>
                <span className="text-gos-muted shrink-0">{(ev.received_at || "").slice(11, 16)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="bg-white border border-gos-border rounded-sm p-4">
      <div className="overline">{label}</div>
      <div className="font-display text-3xl text-gos-charcoal mt-1">{value}</div>
    </div>
  );
}
